/**
 * Position Detail -- Loan Participations position drill-down.
 *
 * Sprint C14 Lane B WP B-7.
 *
 * Loads:
 *  - `GET /api/v1/syndication/positions/{positionId}` for position core
 *  - `GET /api/v1/syndication/offerings/{offeringId}/distributions` for
 *    the distribution history (interest, principal, final)
 *
 * Both endpoints are caller-scoped (post A-5 role-gate split). The
 * distribution list is cross-referenced against the position's
 * `distributions` field (already inlined when present) and falls back
 * to the offering-level history when the inline list is absent.
 *
 * An owner that is not the offering's lead reads `ownDistributions` in place
 * of `distributions` (GH #3511): its own allocation from each event, with no
 * event total and no other holder's line. The page then shows that history
 * and makes no offering-level distributions read.
 */

import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import {
  DataGrid,
  Skeleton,
  Heading,
  Card,
  Badge,
  MetricCard,
  MetricCardGrid,
  Button,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import { useQuery } from '@tanstack/react-query';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import { loanSyndicationClient } from '../../api/client';
import {
  formatCalendarDate,
  formatCurrencyFull,
  formatPercent,
  formatRatioPercent,
  formatCurrencyCompact as formatCurrency,
} from './format';

type ParticipationPosition = LspComponents['schemas']['ParticipationPosition'];
type DistributionEvent = LspComponents['schemas']['DistributionEvent'];
type OwnDistributionLine = LspComponents['schemas']['OwnDistributionLine'];

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getDistributionStatusVariant(
  status: DistributionEvent['status'],
): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'DISTRIBUTED':
      return 'success';
    case 'CRT_MINTED':
      return 'info';
    case 'FIAT_DEPOSITED':
      return 'info';
    case 'INITIATED':
      return 'warning';
    case 'FAILED':
      return 'error';
    default:
      return 'neutral';
  }
}

function getPositionStatusVariant(
  status: ParticipationPosition['status'],
): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'MATURED':
      return 'neutral';
    case 'DEFAULTED':
      return 'error';
    case 'EXITED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function usePosition(positionId: string | undefined) {
  return useQuery({
    queryKey: ['lsp-position', positionId],
    queryFn: async () => {
      if (!positionId) throw new Error('positionId required');
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/positions/{positionId}',
        {
          params: { path: { positionId } },
        },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Failed to load position',
        );
      }
      return data;
    },
    enabled: !!positionId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

function useDistributions(offeringId: string | undefined) {
  return useQuery({
    queryKey: ['lsp-distributions', offeringId],
    queryFn: async () => {
      if (!offeringId) throw new Error('offeringId required');
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/offerings/{offeringId}/distributions',
        {
          params: {
            path: { offeringId },
            query: { page: 1, pageSize: 50 },
          },
        },
      );
      if (error) {
        // Distributions endpoint may 403 for non-LEAD callers in some
        // configurations. Surface an empty list rather than crash --
        // the position detail still has its inline distributions array.
        return { distributions: [] as DistributionEvent[] };
      }
      return data;
    },
    enabled: !!offeringId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

export function PositionDetail() {
  const { positionId } = useParams<{ positionId: string }>();
  const navigate = useNavigate();

  const {
    data: position,
    isLoading: positionLoading,
    error: positionError,
  } = usePosition(positionId);

  const offeringId = position?.offeringId;
  const ownLines = position?.ownDistributions;
  const isOwnerView = ownLines !== undefined;
  const { data: distributionsData, isLoading: distLoading } =
    useDistributions(isOwnerView ? undefined : offeringId);

  // Prefer offering-level distribution history when available; fall
  // back to the position's inline distributions field.
  const distributions = useMemo<DistributionEvent[]>(() => {
    const offeringList = distributionsData?.distributions ?? [];
    if (offeringList.length > 0) return offeringList;
    return position?.distributions ?? [];
  }, [distributionsData, position]);

  const totalDistributed = useMemo(
    () => distributions.reduce((sum, d) => sum + (d.netAmount ?? 0), 0),
    [distributions],
  );

  const ownTotal = useMemo(
    () => (ownLines ?? []).reduce((sum, l) => sum + l.amount, 0),
    [ownLines],
  );

  const ownColumns = useMemo<ColumnDef<OwnDistributionLine, unknown>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Distribution date',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDate(getValue() as string)}
          </span>
        ),
        size: 160,
      },
      {
        accessorKey: 'distributionType',
        header: 'Type',
        cell: ({ getValue }) => (
          <Badge variant="info" size="sm">
            {getValue() as string}
          </Badge>
        ),
        size: 130,
      },
      {
        id: 'paymentPeriod',
        header: 'Period',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatCalendarDate(row.original.paymentPeriodStart)} to{' '}
            {formatCalendarDate(row.original.paymentPeriodEnd)}
          </span>
        ),
        size: 240,
      },
      {
        accessorKey: 'amount',
        header: 'Your amount',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatCurrencyFull(getValue() as number)}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'participationShare',
        header: 'Your share',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatRatioPercent(getValue() as number)}
          </span>
        ),
        size: 110,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge
            variant={getDistributionStatusVariant(row.original.status)}
            size="sm"
          >
            {row.original.status}
          </Badge>
        ),
        size: 150,
      },
    ],
    [],
  );

  const distributionColumns = useMemo<ColumnDef<DistributionEvent, unknown>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Distribution date',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDate(getValue() as string)}
          </span>
        ),
        size: 160,
      },
      {
        accessorKey: 'distributionType',
        header: 'Type',
        cell: ({ getValue }) => (
          <Badge variant="info" size="sm">
            {getValue() as string}
          </Badge>
        ),
        size: 130,
      },
      {
        accessorKey: 'totalAmount',
        header: 'Gross',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatCurrencyFull(getValue() as number)}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'servicingFee',
        header: 'Servicing fee',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatCurrencyFull(getValue() as number | undefined)}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'netAmount',
        header: 'Net',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatCurrencyFull(getValue() as number)}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'recipientCount',
        header: 'Recipients',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {getValue() as number}
          </span>
        ),
        size: 110,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge
            variant={getDistributionStatusVariant(row.original.status)}
            size="sm"
          >
            {row.original.status}
          </Badge>
        ),
        size: 150,
      },
    ],
    [],
  );

  if (!positionId) {
    return (
      <Card className="p-6">
        <div className="text-sm font-sans text-error">
          Missing positionId in route params.
        </div>
      </Card>
    );
  }

  if (positionLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-64 mb-4" />
        <MetricCardGrid className="mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </MetricCardGrid>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (positionError) {
    return (
      <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
        <div>
          <div className="text-sm font-sans text-error">
            Failed to load position.
          </div>
          <div className="text-xs font-sans text-error/80 mt-1">
            {positionError instanceof Error
              ? positionError.message
              : 'Unknown error'}
          </div>
        </div>
      </div>
    );
  }

  if (!position) {
    return (
      <Card className="p-6">
        <div className="text-sm font-sans text-secondary">
          Position not found.
        </div>
      </Card>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="inline-flex items-center gap-2 text-sm font-sans text-tertiary hover:text-primary transition-colors"
        onClick={() => navigate('/syndication/positions')}
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Positions
      </button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 mt-3">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the status badges
              and the identifier subtitle stay in the body, no redundant H1. */}
          <div className="flex items-center gap-3 mb-1">
            <Badge variant={getPositionStatusVariant(position.status)} size="sm">
              {position.status}
            </Badge>
            <Badge
              variant={position.role === 'LEAD' ? 'info' : 'neutral'}
              size="sm"
            >
              {position.role}
            </Badge>
          </div>
          <p className="text-sm font-mono text-tertiary tabular-nums">
            {position.positionId}
          </p>
        </div>
        {position.role === 'PARTICIPANT' && position.status === 'ACTIVE' && (
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/syndication/marketplace')}
          >
            Browse Marketplace
          </Button>
        )}
      </div>

      <MetricCardGrid className="mb-6">
        <MetricCard
          label="Committed"
          value={formatCurrency(position.committedAmount)}
          change={formatRatioPercent(position.participationShare) + ' share'}
          changeType="neutral"
        />
        <MetricCard
          label="Interest Earned"
          value={formatCurrency(position.totalInterestReceived)}
          change="Cumulative"
          changeType="positive"
        />
        <MetricCard
          label="Principal Returned"
          value={formatCurrency(position.totalPrincipalReturned)}
          change="Cumulative"
          changeType="neutral"
        />
        <MetricCard
          label="Annualized Yield"
          value={formatPercent(position.annualizedYield)}
          change={
            position.nextDistributionDate
              ? `Next ${formatDate(position.nextDistributionDate)}`
              : 'No upcoming distribution'
          }
          changeType="neutral"
        />
      </MetricCardGrid>

      <Card className="mb-6 p-6">
        <Heading level={2} size="card" className="mb-4">
          Loan & Collateral
        </Heading>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-sans">
          <div>
            <div className="text-xs text-tertiary mb-1">Loan amount</div>
            <div className="font-mono text-primary tabular-nums">
              {formatCurrencyFull(position.loanDetails?.loanAmount)}
            </div>
          </div>
          <div>
            <div className="text-xs text-tertiary mb-1">Rate</div>
            <div className="font-mono text-primary tabular-nums">
              {formatPercent(position.loanDetails?.interestRate)}
            </div>
          </div>
          <div>
            <div className="text-xs text-tertiary mb-1">Term</div>
            <div className="font-mono text-primary tabular-nums">
              {position.loanDetails?.termMonths ?? '--'}{' '}
              {position.loanDetails?.termMonths ? 'months' : ''}
            </div>
          </div>
          <div>
            <div className="text-xs text-tertiary mb-1">Loan type</div>
            <div className="text-primary">
              {position.loanDetails?.loanType ?? '--'}
            </div>
          </div>
          <div>
            <div className="text-xs text-tertiary mb-1">Collateral value</div>
            <div className="font-mono text-primary tabular-nums">
              {formatCurrencyFull(position.collateralSummary?.totalValue)}
            </div>
          </div>
          <div>
            <div className="text-xs text-tertiary mb-1">LTV</div>
            <div className="font-mono text-primary tabular-nums">
              {formatRatioPercent(position.collateralSummary?.ltvRatio)}
            </div>
          </div>
          <div>
            <div className="text-xs text-tertiary mb-1">Asset class</div>
            <div className="text-primary">
              {position.collateralSummary?.primaryAssetClass ?? '--'}
            </div>
          </div>
          <div>
            <div className="text-xs text-tertiary mb-1">Asset count</div>
            <div className="font-mono text-primary tabular-nums">
              {position.collateralSummary?.assetCount ?? '--'}
            </div>
          </div>
        </div>
      </Card>

      <Heading level={2} size="card" className="mb-4">
        Distribution History
      </Heading>
      {ownLines !== undefined ? (
        <>
          <div className="text-xs font-sans text-tertiary mb-3">
            {ownLines.length} event{ownLines.length === 1 ? '' : 's'},{' '}
            {formatCurrencyFull(ownTotal)} allocated to this position.
          </div>
          {ownLines.length === 0 ? (
            <Card className="p-8 text-center">
              <div className="text-sm font-sans text-secondary">
                No distributions recorded yet for this position.
              </div>
            </Card>
          ) : (
            <DataGrid<OwnDistributionLine>
              data={ownLines}
              columns={ownColumns}
              enablePagination
              defaultPageSize={25}
              hoverable
              getRowId={(row) => row.distributionId}
              className="w-full rounded-sm shadow-sm"
            />
          )}
        </>
      ) : (
        <>
          <div className="text-xs font-sans text-tertiary mb-3">
            {distributions.length} event{distributions.length === 1 ? '' : 's'},{' '}
            {formatCurrencyFull(totalDistributed)} net distributed.
          </div>

          {distLoading ? (
            <Card className="p-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full mb-2" />
              ))}
            </Card>
          ) : distributions.length === 0 ? (
            <Card className="p-8 text-center">
              <div className="text-sm font-sans text-secondary">
                No distributions recorded yet for this position.
              </div>
            </Card>
          ) : (
            <DataGrid<DistributionEvent>
              data={distributions}
              columns={distributionColumns}
              enablePagination
              defaultPageSize={25}
              hoverable
              getRowId={(row) => row.distributionId}
              className="w-full rounded-sm shadow-sm"
            />
          )}
        </>
      )}
    </div>
  );
}
